import {
  getCampaigns,
  getCampaign,
  getCampaignEnabled,
  newCampaign,
  getBonusAssignments,
  publishCampaign,
  deleteCampaign,
  updateCampaign,
} from '@services/queries'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { store } from 'react-notifications-component'
import {
  CAMPAIGN_CREATION_SUCCESS_MESSAGE,
  CAMPAIGN_CREATION_FAILURE_MESSAGE,
  CAMPAIGN_ENABLE_SUCCESS_MESSAGE,
  CAMPAIGN_ENABLE_FAILURE_MESSAGE,
  CAMPAIGN_PUBLISH_SUCCESS_MESSAGE,
  CAMPAIGN_PUBLISH_FAILURE_MESSAGE,
  CAMPAIGN_DELETE_SUCCESS_MESSAGE,
  CAMPAIGN_DELETE_FAILURE_MESSAGE,
  CAMPAIGN_UPDATE_SUCCESS_MESSAGE,
  CAMPAIGN_UPDATE_FAILURE_MESSAGE,
} from '@constants'

export function useCampaigns(filters) {
  const { name, pageSize, pageNumber, isDraft, isEnabled, scheduleStatus } =
    filters
  const info = useQuery(
    [
      'campaigns',
      name,
      pageSize,
      pageNumber,
      isDraft,
      isEnabled,
      scheduleStatus,
    ],
    () => getCampaigns(filters),
  )
  return info
}

export function useCampaign(campaignId) {
  const info = useQuery(['campaign', campaignId], () => getCampaign(campaignId))

  return info
}

export function useCampaignEnabledMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    ([enabled, campaignId]) => {
      return getCampaignEnabled(enabled, campaignId)
    },
    {
      onSuccess(data, variables) {
        queryClient.invalidateQueries(['campaigns'])
        queryClient.invalidateQueries(['campaign', variables[1]])
        if (onSuccess) onSuccess(data)
        store.addNotification(CAMPAIGN_ENABLE_SUCCESS_MESSAGE)
      },
      onError(err) {
        console.error('ERROR UPDATING CAMPAIGN', err)
        store.addNotification(CAMPAIGN_ENABLE_FAILURE_MESSAGE)
        if (onError) onError(err)
      },
    },
  )

  return mutationInfo
}

export function useBonusAssignments(bonusId) {
  return useQuery(
    ['bonusAssignments', bonusId],
    () => getBonusAssignments(bonusId),
    { enabled: !!bonusId },
  )
}

export function useNewCampaignMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    (campaign) => {
      return newCampaign(campaign.campaign)
    },
    {
      onSuccess(data, campaign) {
        queryClient.invalidateQueries(['campaigns'])
        campaign.setStatusSubmit({ status: true })
        if (onSuccess) onSuccess(data)
        store.addNotification(CAMPAIGN_CREATION_SUCCESS_MESSAGE)
      },
      onError(err, campaign) {
        campaign.setStatusSubmit({ status: false })
        console.error('ERROR UPDATING CAMPAIGN', err)
        store.addNotification(CAMPAIGN_CREATION_FAILURE_MESSAGE)
        if (onError) onError(err)
      },
    },
  )

  return mutationInfo
}

export function usePublishCampaignMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  return useMutation((id) => publishCampaign(id), {
    onSuccess(data, id) {
      queryClient.invalidateQueries(['campaigns'])
      queryClient.invalidateQueries(['campaign', id])
      store.addNotification(CAMPAIGN_PUBLISH_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data, id)
    },
    onError(err, id) {
      store.addNotification(CAMPAIGN_PUBLISH_FAILURE_MESSAGE)
      if (onError) onError(err, id)
    },
  })
}

export function useDeleteCampaignMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  return useMutation((id) => deleteCampaign(id), {
    onSuccess(data, id) {
      queryClient.invalidateQueries(['campaigns'])
      store.addNotification(CAMPAIGN_DELETE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data, id)
    },
    onError(err, id) {
      store.addNotification(CAMPAIGN_DELETE_FAILURE_MESSAGE)
      if (onError) onError(err, id)
    },
  })
}

export function useUpdateCampaignMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  return useMutation(({ id, payload }) => updateCampaign(id, payload), {
    onSuccess(data, { id }) {
      queryClient.invalidateQueries(['campaigns'])
      queryClient.invalidateQueries(['campaign', id])
      store.addNotification(CAMPAIGN_UPDATE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data, id)
    },
    onError(err, { id }) {
      store.addNotification(CAMPAIGN_UPDATE_FAILURE_MESSAGE)
      if (onError) onError(err, id)
    },
  })
}
